"use client"

import { ColumnDef } from "@tanstack/react-table"
import { Button } from "@/components/shadcn/button"
import { Badge } from "@/components/shadcn/badge"
import { Checkbox } from "@/components/shadcn/checkbox"
import { MoreHorizontal, Trash2 } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu"
import { TFunction } from "i18next"

// 记忆消息数据类型
export type MemoryMessage = {
  id: string
  time_iso: string
  role: string
  content: Array<{
    type: string
    text?: string | null
    url?: string | null
  }>
}

export const createMemoriesColumns = (t: TFunction): ColumnDef<MemoryMessage>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={t('memory.detail.table.selectAll')}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={t('memory.detail.table.selectRow')}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "time_iso",
    header: t('memory.detail.table.time'),
    cell: ({ row }) => {
      const time = row.getValue("time_iso") as string
      return (
        <div className="text-sm text-muted-foreground">
          {new Date(time).toLocaleString('zh-CN')}
        </div>
      )
    },
  },
  {
    accessorKey: "role",
    header: t('memory.detail.table.role'),
    cell: ({ row }) => {
      const role = row.getValue("role") as string
      return (
        <Badge variant="secondary" className="text-xs">
          {role}
        </Badge>
      )
    },
  },
  {
    accessorKey: "content",
    header: t('memory.detail.table.content'),
    cell: ({ row }) => {
      const content = row.getValue("content") as Array<{ type: string; text?: string; url?: string }>
      const textParts = content
        .map((c) => c.type === 'text' ? (c.text || '') : (c.url ? `[链接] ${c.url}` : ''))
        .filter(Boolean)
      const contentText = textParts.join(' \n ')
      
      return (
        <div className="max-w-[600px]">
          <div className="truncate" title={contentText}>
            {contentText}
          </div>
        </div>
      )
    },
  },
  {
    id: "actions",
    header: t('memory.detail.table.actions'),
    cell: ({ row }) => {
      const messageId = row.getValue("id") as string
      
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">{t('memory.detail.table.openMenu')}</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                // 触发删除消息的逻辑
                const event = new CustomEvent('deleteMessage', { detail: { messageId } })
                window.dispatchEvent(event)
              }}
              className="text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('memory.detail.table.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
  },
]
